<div
  class="flex flex-col rounded-2xl p-2"
  [ngClass]="{
    'tile-container cursor-pointer shadow-sm hover:shadow-lg transition-all duration-200 ease-in-out': clickable(),
    'h-full': briefingTimeline(),
    'max-md:p-3': creatorBriefingMode(),
  }"
  [style.background]="platformFeatureCardColor()"
  (click)="onTileClick()"
>
  <div class="flex flex-col justify-between gap-2">
    <!-- Content Section -->
    <div class="flex flex-col gap-2">
      <!-- Header Section -->
      <div
        class="flex justify-between gap-2 text-sm rounded-tr-2xl rounded-tl-2xl -mt-2 -mx-2 p-2 border"
        [ngClass]="{ 'max-md:-mt-3 max-md:-mx-3 max-md:px-3 max-md:py-2.5': creatorBriefingMode() }"
        [style.background-color]="platformFeatureHeaderBackgroundColor()"
        [style.border-color]="platformFeatureHeaderBorderColor()"
      >
        @if (creatorBriefingMode() && briefingOverview()) {
          <div class="flex items-center gap-1">
            @if (tileConfiguration().executionType === EExecutionType.always) {
              <span class="text-[10px] max-md:text-xs font-medium text-shikenso-purple-base">
                {{ 'pages.campaignDetails.creatorOverview.alwaysRequired' | translate }}
              </span>
            } @else if (tileConfiguration().executionType === EExecutionType.unique) {
              <span class="text-[10px] max-md:text-xs font-medium text-shikenso-purple-base">
                {{ executionTimeDisplay() }}
              </span>
            } @else if (tileConfiguration().executionType === EExecutionType.timeFrame) {
              <span
                class="text-[10px] max-md:text-xs font-medium text-shikenso-purple-base"
                matTooltipPosition="above"
                [matTooltip]="timeFrameTooltip()"
              >
                {{ briefingTimeFrameLabel() }} · {{ tileConfiguration().remainingExecutionCount }}
                {{ 'pages.campaignDetails.creatorOverview.moreRequired' | translate }}
              </span>
            } @else if (tileConfiguration().executionType === EExecutionType.noExpectations) {
              <span class="text-[10px] max-md:text-xs font-medium text-shikenso-purple-base">
                {{ 'pages.campaignDetails.creatorOverview.trackingOnly' | translate }}
              </span>
            } @else {
              <span class="text-[10px] max-md:text-xs font-medium text-shikenso-purple-base">
                {{ tileConfiguration().remainingExecutionCount }}
                {{ 'pages.campaignDetails.creatorOverview.moreRequired' | translate }}
              </span>
            }
          </div>
        } @else if (!creatorBriefingMode()) {
          <div class="flex items-center gap-1">
            <app-icon-badge
              alt="Creator"
              backgroundColor="transparent"
              [content]="tileConfiguration().creator.icon"
              [fallbackText]="tileConfiguration().creator.name"
              [size]="24"
              [iconSize]="24"
              [fontSize]="12"
              [borderRadius]="100"
            />
            <p
              #creatorName
              matTooltip
              matTooltipPosition="above"
              class="line-clamp-1 text-ellipsis text-base text-shikenso-base-white font-medium"
              [class.max-w-44]="!executionTimeDisplay()"
              [class.max-w-[76px]]="executionTimeDisplay()"
              [style.color]="creatorNameColor()"
              [appTextClampTooltip]="tileConfiguration().creator.name"
            >
              {{ tileConfiguration().creator.name }}
            </p>
          </div>
        }

        <div class="flex items-center gap-2" [ngClass]="{ 'w-full justify-between': briefingTimeline() }">
          @if ((executionTimeDisplay() || briefingTimeline()) && !briefingOverview()) {
            <p
              class="text-[10px] text-shikenso-purple-base leading-none"
              matTooltipPosition="above"
              [matTooltip]="timeFrameTooltip()"
              [ngClass]="{ 'max-md:text-xs': creatorBriefingMode() }"
            >
              {{ executionTimeDisplay() }}
            </p>
          }

          <app-social-badge
            matTooltipPosition="above"
            backgroundColor="transparent"
            [size]="16"
            [iconSize]="16"
            [color]="platformFeatureIconColor()"
            [matTooltip]="tileConfiguration().nonExecutedCtaName"
            [type]="tileConfiguration().platformFeature"
            [showTooltip]="!tileConfiguration().nonExecutedCtaName"
          />
        </div>
      </div>

      @if (compactRow()) {
        <!-- Compact content row (mobile briefing timeline and dialog): thumbnail on the left, status and title on the right -->
        <div class="flex gap-3" data-testid="social-content-tile-compact">
          @if (compactThumbnailType(); as thumbnailType) {
            <div class="relative w-24 h-24 shrink-0 rounded-lg overflow-hidden bg-shikenso-gray-base-24">
              @if (thumbnailType === 'video') {
                <video
                  class="w-full h-full object-cover pointer-events-none"
                  preload="metadata"
                  muted
                  playsinline
                  [src]="tileConfiguration().media![0] + '#t=0.1'"
                ></video>
              } @else if (thumbnailType === 'image') {
                <img
                  class="w-full h-full object-cover"
                  loading="lazy"
                  [alt]="tileConfiguration().creator.name"
                  [src]="tileConfiguration().media![0]"
                />
              } @else {
                <div class="flex items-center justify-center w-full h-full">
                  <app-social-badge
                    backgroundColor="transparent"
                    [size]="32"
                    [iconSize]="32"
                    [color]="platformFeatureIconColor()"
                    [type]="tileConfiguration().platformFeature"
                    [showTooltip]="false"
                  />
                </div>
              }

              @if (tileConfiguration().isLive) {
                <div class="absolute top-1 right-1 w-2 h-2 bg-shikenso-error-base rounded-full"></div>
              }
            </div>
          }

          <div class="flex flex-col gap-1.5 min-w-0 flex-1">
            <ng-container *ngTemplateOutlet="statusBadges" />

            @if (tileConfiguration().duration !== undefined && tileConfiguration().duration !== null) {
              <div class="flex items-center gap-1">
                <app-icon src="icons/clock.svg" alt="Duration" [size]="13" />
                <span class="text-xs">{{ tileConfiguration().duration! | duration }}</span>
              </div>
            }

            @if (tileConfiguration().title) {
              <p class="font-bold text-[13px] dynamic-line-clamp" [style.--line-clamp]="2">
                {{ tileConfiguration().title }}
              </p>
            } @else if (tileConfiguration().description) {
              <p class="text-[13px] dynamic-line-clamp" [style.--line-clamp]="4">
                {{ tileConfiguration().description }}
              </p>
            }
          </div>
        </div>
      } @else {
        <!-- Media and Status Section -->
        <div class="flex flex-col gap-2 relative">
          @if (
            tileConfiguration().media &&
            tileConfiguration().media!.length > 0 &&
            tileConfiguration()!.media![0] !== null
          ) {
            <div class="carousel-container">
              <app-carousel [configuration]="carouselConfiguration()" (click)="onCarouselClick($event)" />
            </div>
          }

          @if (tileConfiguration().isLive) {
            <div class="absolute top-1 right-1 w-2 h-2 bg-shikenso-error-base rounded-full z-10"></div>
          }

          <ng-container *ngTemplateOutlet="statusBadges" />
        </div>
      }

      <!-- Planned/Missing Content Section -->
      @if (
        tileConfiguration().matchingType === EMatchingType.planned ||
        tileConfiguration().matchingType === EMatchingType.missing
      ) {
        @if (tileConfiguration().nonExecutedCtaName) {
          <p class="text-sm" [ngClass]="{ 'max-md:text-base': creatorBriefingMode() }">
            {{ tileConfiguration().nonExecutedCtaName }}
          </p>
        }

        @let hasDeliverables = tileConfiguration().deliverableTypeNames.length > 0;
        @let hasKpis = tileConfiguration().expectedKpis.length > 0;

        @if (hasDeliverables || hasKpis) {
          <hr class="border-shikenso-purple-base-12 -mx-2" [ngClass]="{ 'max-md:-mx-3': creatorBriefingMode() }" />
        }

        @if (hasDeliverables) {
          <div
            class="flex gap-1 text-xs"
            [class.flex-col]="!briefingOverview()"
            [ngClass]="{ 'max-md:text-[13px]': creatorBriefingMode() }"
          >
            <p [class.py-1]="briefingOverview()" [class.pl-1]="briefingOverview()">
              {{ 'shared.tile.deliverables' | translate }}:
            </p>

            @if (!briefingOverview()) {
              <ul class="list-disc list-inside pl-2">
                @if (tileConfiguration().deliverableTypeNames.length <= 4) {
                  @for (name of tileConfiguration().deliverableTypeNames; track $index) {
                    <li>{{ name }}</li>
                  }
                } @else {
                  @for (name of tileConfiguration().deliverableTypeNames.slice(0, 3); track $index) {
                    <li>{{ name }}</li>
                  }

                  <li>
                    {{
                      'shared.tile.moreItems'
                        | translate: { count: tileConfiguration().deliverableTypeNames.length - 3 }
                    }}
                  </li>
                }
              </ul>
            } @else {
              <p class="py-1">{{ briefingDeliverableLabels() }}</p>
            }
          </div>
        }

        @if (hasKpis && !briefingOverview()) {
          <div class="flex flex-col gap-1 text-xs" [ngClass]="{ 'max-md:text-[13px]': creatorBriefingMode() }">
            <p>{{ 'shared.tile.kpiGoals' | translate }}:</p>

            @if (!briefingOverview()) {
              <ul class="list-disc list-inside pl-2">
                @if (tileConfiguration().expectedKpis.length <= 4) {
                  @for (kpi of tileConfiguration().expectedKpis; track kpi.name) {
                    <li>{{ kpi.name }}: {{ kpi.value | numberSuffix }}</li>
                  }
                } @else {
                  @for (kpi of tileConfiguration().expectedKpis.slice(0, 3); track kpi.name) {
                    <li>{{ kpi.name }}: {{ kpi.value | numberSuffix }}</li>
                  }

                  <li>
                    {{ 'shared.tile.moreItems' | translate: { count: tileConfiguration().expectedKpis.length - 3 } }}
                  </li>
                }
              </ul>
            }
          </div>
        }
      } @else if (!compactRow()) {
        <!-- Duration Section -->
        @if (tileConfiguration().duration !== undefined && tileConfiguration().duration !== null) {
          <div class="flex items-center gap-1">
            <app-icon src="icons/clock.svg" alt="Duration" [size]="12" />
            <span class="text-xs">{{ tileConfiguration().duration! | duration }}</span>
          </div>
        }

        <!-- Title and Description Section -->
        @if (tileConfiguration().title || tileConfiguration().description) {
          <div class="flex flex-col gap-2">
            @if (tileConfiguration().title) {
              <p
                #title
                class="font-bold text-ellipsis dynamic-line-clamp text-xs"
                matTooltipPosition="above"
                [matTooltip]="tileConfiguration().title"
                [matTooltipDisabled]="!isTitleClamped()"
                [style.--line-clamp]="platformFeatureLineClampValues().title"
              >
                {{ tileConfiguration().title }}
              </p>
            }

            @if (tileConfiguration().description) {
              <p
                #description
                class="text-ellipsis dynamic-line-clamp text-xs"
                matTooltipPosition="above"
                matTooltipClass="description-tooltip"
                [matTooltip]="tileConfiguration().description"
                [matTooltipDisabled]="!isDescriptionClamped()"
                [style.--line-clamp]="platformFeatureLineClampValues().description"
              >
                {{ tileConfiguration().description }}
              </p>
            }
          </div>
        }

        <!-- KPI Values Section -->
        @if (
          tileConfiguration().executionStatus !== EMatchingType.planned &&
          canDisplayAnyKpi() &&
          tileConfiguration().kpis.length &&
          !dialogMode()
        ) {
          <div class="flex gap-2 text-[10px] mt-1 w-full">
            @for (kpi of tileConfiguration().kpis; track kpi.id) {
              @if (kpi.value !== null && (!noCampaignContextMode() || kpi.kpiKey !== 'mediaValue')) {
                <div class="flex items-center gap-0.5" matTooltipPosition="above" [matTooltip]="kpi.kpiName">
                  <app-icon-badge
                    alt="KPI"
                    backgroundColor="transparent"
                    color="var(--shikenso-purple-base)"
                    [fontSize]="12"
                    [fallbackTextFontWeight]="300"
                    [content]="kpi.icon"
                    [size]="16"
                    [iconSize]="16"
                    [borderRadius]="0"
                  />

                  @if (kpi.isPercentage) {
                    {{ kpi.value | percentage: true }}
                  } @else {
                    {{ kpi.value | numberSuffix }}
                  }

                  @if (kpi.suffix) {
                    <span class="opacity-50">{{ kpi.suffix }}</span>
                  }
                </div>
              }
            }
          </div>
        }

        <!-- Problem Indicators / Bonus Deliverables Section -->
        @if (!noCampaignContextMode() && !dialogMode() && !creatorBriefingMode()) {
          @let matched = tileConfiguration().matchingType === EMatchingType.matched;
          @let bonus = tileConfiguration().matchingType === EMatchingType.bonus;
          @let isUnrelated = tileConfiguration().matchingType === EMatchingType.unrelated;
          @let hasFailedDeliverables = tileConfiguration().ctaKpiStatusable.ctaKpiStatus.currentFailedDeliverables > 0;
          @let hasProblemDeliverables =
            tileConfiguration().ctaKpiStatusable.ctaKpiStatus.currentProblemDeliverables > 0;
          @let hasProblemKpis = tileConfiguration().ctaKpiStatusable.ctaKpiStatus.currentProblemKpis > 0;
          @let hasFailedKpis = tileConfiguration().ctaKpiStatusable.ctaKpiStatus.currentFailedKpis > 0;
          @let bonusOrHasObservation = bonus || (isUnrelated && tileConfiguration().observationCount > 0);

          @if (
            (matched && (hasFailedDeliverables || hasProblemDeliverables || hasProblemKpis || hasFailedKpis)) ||
            bonus ||
            (isUnrelated && tileConfiguration().observationCount > 0)
          ) {
            <hr class="border-shikenso-purple-base-12 -mx-2" />
          }

          @if (matched && (hasFailedDeliverables || hasProblemDeliverables || hasProblemKpis || hasFailedKpis)) {
            <div class="flex flex-col gap-1 text-xs pl-2">
              @if (hasFailedDeliverables) {
                <div class="flex items-center gap-3">
                  <span class="w-3 h-3 rounded-full flex items-center justify-center bg-shikenso-error-bright">
                    <app-icon src="icons/close.svg" alt="Failed" color="white" [size]="10" />
                  </span>
                  <span>
                    {{
                      'shared.tile.failedDeliverables'
                        | translate
                          : { count: tileConfiguration().ctaKpiStatusable.ctaKpiStatus.currentFailedDeliverables }
                    }}
                  </span>
                </div>
              }

              @if (hasProblemDeliverables) {
                <div class="flex items-center gap-3">
                  <span class="w-3 h-3 rounded-full flex items-center justify-center bg-shikenso-warning-base">
                    <app-icon src="icons/backslash.svg" alt="Problem" color="white" [size]="10" />
                  </span>
                  <span>
                    {{
                      'shared.tile.problematicDeliverables'
                        | translate
                          : { count: tileConfiguration().ctaKpiStatusable.ctaKpiStatus.currentProblemDeliverables }
                    }}
                  </span>
                </div>
              }

              @if (hasProblemKpis) {
                <div class="flex items-center gap-3">
                  <span class="w-3 h-3 rounded-full flex items-center justify-center bg-shikenso-warning-base">
                    <app-icon src="icons/backslash.svg" alt="KPI Below" color="white" [size]="10" />
                  </span>
                  <span>
                    {{
                      'shared.tile.kpiBelow'
                        | translate: { count: tileConfiguration().ctaKpiStatusable.ctaKpiStatus.currentProblemKpis }
                    }}
                  </span>
                </div>
              }

              @if (hasFailedKpis) {
                <div class="flex items-center gap-3">
                  <span class="w-3 h-3 rounded-full flex items-center justify-center bg-shikenso-error-base">
                    <app-icon src="icons/close.svg" alt="KPI Failed" color="white" [size]="10" />
                  </span>
                  <span>
                    {{
                      'shared.tile.kpiFailed'
                        | translate: { count: tileConfiguration().ctaKpiStatusable.ctaKpiStatus.currentFailedKpis }
                    }}
                  </span>
                </div>
              }
            </div>
          }

          @if (bonusOrHasObservation) {
            <div class="flex flex-col gap-1">
              @if (bonus) {
                <p class="text-xs pl-2">
                  {{
                    'shared.tile.bonusDeliverables'
                      | translate
                        : {
                            current: tileConfiguration().ctaKpiStatusable.ctaKpiStatus.currentBonusDeliverables,
                            expected:
                              tileConfiguration().ctaKpiStatusable.ctaKpiStatus.expectedDeliverables > 0
                                ? tileConfiguration().ctaKpiStatusable.ctaKpiStatus.expectedDeliverables
                                : tileConfiguration().ctaKpiStatusable.ctaKpiStatus.totalDeliverables,
                          }
                  }}
                </p>
              }

              @if (tileConfiguration().observationCount > 0) {
                <p class="text-xs pl-2">
                  {{ 'shared.tile.observations' | translate: { count: tileConfiguration().observationCount } }}
                </p>
              }
            </div>
          }
        }
      }
    </div>
  </div>
</div>

<ng-template #statusBadges>
  @if (!noCampaignContextMode()) {
    <div class="flex items-center gap-1 flex-wrap">
      @if (tileConfiguration().matchingType === EMatchingType.matched && tileConfiguration().contentStatus) {
        <app-status-badge colorScheme="content-status" size="small" [status]="tileConfiguration().contentStatus!" />
      }

      @if (!briefingTimeline() || tileConfiguration().matchingType !== EMatchingType.matched) {
        <app-status-badge
          [size]="creatorBriefingMode() ? 'small' : 'micro'"
          [colorScheme]="creatorBriefingMode() ? 'matching-status-borderless' : 'matching-status'"
          [status]="tileConfiguration().matchingType"
        />
      }
    </div>
  }
</ng-template>
